import { motion } from 'motion/react'
import { Skeleton } from '@/components/ui/skeleton'
import { SLOT_COLORS } from '@/components/dashboard/PartToWholeBreakdown'
import { DISCIPLINE_TABS } from '@/components/dashboard/DisciplineDock'
import { CountUp } from '@/components/dashboard/fx/CountUp'
import { Reveal } from '@/components/dashboard/fx/Reveal'
import { SpotlightCard } from '@/components/dashboard/fx/SpotlightCard'
import { EASE } from '@/components/dashboard/fx/motion'
import { useCatalogCoverage, type CatalogCoverage as Coverage } from '@/hooks/useCatalogCoverage'
import { formatPrecise } from '@/lib/format'

// "2018-11-16" -> a local date (parsing the bare string would shift it a day
// back in timezones behind UTC).
function parseDay(iso: string) {
  const [year, month, day] = iso.split('-').map(Number)
  return new Date(year, month - 1, day)
}

const fullDate = (iso: string) => parseDay(iso).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
const monthYear = (iso: string) => parseDay(iso).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })

const disciplineLabel = (key: string) => DISCIPLINE_TABS.find((tab) => tab.value === key)?.label ?? key.charAt(0).toUpperCase() + key.slice(1)

function Stat({ children, label }: { children: React.ReactNode; label: string }) {
  return (
    <div className="flex flex-col gap-1">
      <span className="font-display text-3xl font-semibold leading-none tracking-tighter sm:text-4xl">{children}</span>
      <span className="text-sm text-muted-foreground">{label}</span>
    </div>
  )
}

function Body({ data }: { data: Coverage }) {
  const colors = Object.fromEntries(data.disciplines.map((d, i) => [d.key, SLOT_COLORS[i % SLOT_COLORS.length]]))
  const max = Math.max(...data.years.map((y) => y.total), 1)
  const currentYear = new Date().getFullYear()
  const playlistPercent = data.total_classes ? Math.round((data.with_playlists / data.total_classes) * 100) : 0

  if (data.total_classes === 0) return <p className="text-muted-foreground">Nothing has been imported yet.</p>

  return (
    <div className="flex flex-col gap-10">
      <div className="grid grid-cols-2 gap-6 lg:grid-cols-4">
        <Stat label="classes imported">
          <CountUp value={data.total_classes} />
        </Stat>
        <Stat label="with a full playlist">
          <CountUp value={playlistPercent} suffix="%" />
        </Stat>
        <Stat label="oldest class aired">{data.oldest ? monthYear(data.oldest.aired_on) : '--'}</Stat>
        <Stat label="newest class aired">{data.newest ? monthYear(data.newest.aired_on) : '--'}</Stat>
      </div>

      <div className="grid gap-10 lg:grid-cols-[minmax(0,1.5fr)_minmax(0,1fr)]">
        <table className="w-full border-collapse text-sm">
          <caption className="mb-3 text-left font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground">Classes by year aired</caption>
          <thead className="sr-only">
            <tr>
              <th scope="col">Year</th>
              <th scope="col">Classes</th>
              <th scope="col">Split by discipline</th>
            </tr>
          </thead>
          <tbody>
            {data.years.map((row, i) => (
              <tr key={row.year} className="group border-b border-border/60 last:border-0" title={Object.entries(row.by_discipline).map(([k, n]) => `${disciplineLabel(k)} ${formatPrecise(n)}`).join(' · ')}>
                <th scope="row" className="w-24 py-1.5 pr-3 text-left font-display text-lg font-semibold tabular-nums">
                  {row.year}
                  {row.year === currentYear && <span className="ml-2 font-sans text-[11px] font-normal text-muted-foreground">so far</span>}
                </th>
                <td className="w-16 py-1.5 pr-4 text-right tabular-nums text-muted-foreground group-hover:text-foreground">{formatPrecise(row.total)}</td>
                <td className="py-1.5">
                  <motion.div
                    aria-hidden
                    className="flex h-3 origin-left gap-px overflow-hidden rounded-full"
                    style={{ width: `${(row.total / max) * 100}%`, minWidth: '0.5rem' }}
                    initial={{ scaleX: 0 }}
                    whileInView={{ scaleX: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.9, delay: i * 0.05, ease: EASE }}
                  >
                    {data.disciplines
                      .filter((d) => row.by_discipline[d.key])
                      .map((d) => (
                        <span key={d.key} style={{ flexGrow: row.by_discipline[d.key], flexBasis: 0, backgroundColor: colors[d.key] }} />
                      ))}
                  </motion.div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="flex flex-col gap-6">
          <table className="h-fit w-full border-collapse text-sm">
            <caption className="mb-3 text-left font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground">By discipline</caption>
            <thead>
              <tr className="text-left text-xs text-muted-foreground">
                <th scope="col" className="pb-2 font-normal">Discipline</th>
                <th scope="col" className="pb-2 text-right font-normal">Classes</th>
                <th scope="col" className="pb-2 pl-4 text-right font-normal">Since</th>
              </tr>
            </thead>
            <tbody>
              {data.disciplines.map((d) => (
                <tr key={d.key} className="border-b border-border/60 last:border-0">
                  <th scope="row" className="py-2.5 pr-3 text-left font-medium">
                    <span className="flex items-center gap-2.5">
                      <span aria-hidden className="size-2.5 shrink-0 rounded-full" style={{ backgroundColor: colors[d.key] }} />
                      {disciplineLabel(d.key)}
                    </span>
                  </th>
                  <td className="py-2.5 text-right tabular-nums">{formatPrecise(d.classes)}</td>
                  <td className="py-2.5 pl-4 text-right tabular-nums text-muted-foreground">{monthYear(d.first_aired)}</td>
                </tr>
              ))}
            </tbody>
          </table>

          {data.oldest && (
            <p className="text-sm text-muted-foreground">
              The oldest class on file is <span className="font-medium text-foreground">{data.oldest.title}</span>
              {data.oldest.instructor && <> with {data.oldest.instructor}</>}, aired {fullDate(data.oldest.aired_on)} ({disciplineLabel(data.oldest.discipline).toLowerCase()}).
            </p>
          )}
        </div>
      </div>
    </div>
  )
}

// How much the catalog holds and how far back it goes -- computed live from
// whatever this environment has imported, so it's always current.
export function CatalogCoverage() {
  const { data, error } = useCatalogCoverage()

  return (
    <Reveal>
      <SpotlightCard className="p-6 sm:p-10">
        <div className="mb-8 flex flex-col gap-2">
          <span className="font-mono text-xs uppercase tracking-[0.25em] text-primary">The data</span>
          <h2 className="font-display text-3xl font-semibold tracking-tighter sm:text-4xl">How far back does it go?</h2>
          <p className="max-w-2xl text-muted-foreground">
            Every class the scraper has imported from Peloton&apos;s on-demand library. There&apos;s no cutoff: it walks the library from newest to oldest until Peloton has nothing older.
          </p>
        </div>

        {error && <p className="text-sm text-destructive">Couldn&apos;t load catalog coverage: {error}</p>}
        {data ? (
          <Body data={data} />
        ) : (
          !error && (
            <div className="flex flex-col gap-4">
              <Skeleton className="h-16 w-full rounded-xl" />
              <Skeleton className="h-64 w-full rounded-xl" />
            </div>
          )
        )}
      </SpotlightCard>
    </Reveal>
  )
}
